import { ExternalLinkIcon } from "@heroicons/react/outline";
import { Link } from "react-router-dom";

import { Badge, BadgeVariant } from "../../Badge";
import { BasicTooltip } from "../../Tooltip";
import { OverflowDisplayTooltip } from "../../Tooltip/OverflowDisplayTooltip";

import { processTag, sortTagsWithLinksFirst } from "@/utils/tagUtils";
import { useUser } from "@/context/user-context";
import { useEvaluateTagReports } from "@/hooks/evaluate/useEvaluateTagReports";

export enum CardTagsSeparator {
  DOT = "•",
  PIPE = "|",
}

interface CardTagsProps {
  tags: string[];
  maxVisibleTags?: number;
  useBadge?: boolean;
  separator?: CardTagsSeparator;
  emptyText?: string;
  shouldShowEmptyText?: boolean;
}

export const CardTags = ({
  tags,
  maxVisibleTags = 3,
  useBadge = true,
  separator,
  emptyText,
  shouldShowEmptyText = false,
}: CardTagsProps) => {
  const userContext = useUser();
  const workspaceId = userContext?.activeWorkspaceId;

  // Fetch metadata for evaluate tags
  const { metadataMap: evaluateMetadataMap } = useEvaluateTagReports(tags);

  if (tags.length === 0) {
    if (emptyText) {
      return (
        <div className="card-tags mt-2 flex min-w-0 max-w-full items-center gap-1 overflow-hidden transition-colors duration-200 ease-in-out">
          <div className="inline-flex text-xs font-medium text-gray-400 transition-colors duration-200 ease-in-out">
            {emptyText}
          </div>
        </div>
      );
    }

    if (shouldShowEmptyText) {
      return (
        <div className="card-tags mt-2 flex min-w-0 max-w-full items-center gap-1 overflow-hidden transition-colors duration-200 ease-in-out">
          <div className="inline-flex h-[22px] text-xs font-medium text-gray-400 transition-colors duration-200 ease-in-out" />
        </div>
      );
    }

    return null;
  }

  const sortedTags = sortTagsWithLinksFirst(tags);
  const displayTags = sortedTags.slice(0, maxVisibleTags);
  const remainingTags = sortedTags.slice(maxVisibleTags);

  return (
    <div className="card-tags mt-2 flex min-w-0 max-w-full items-center gap-1 overflow-hidden transition-colors duration-200 ease-in-out">
      {displayTags.map((tag: string, idx: number) => {
        const {
          isLink,
          displayText,
          linkPath: tagLinkPath,
        } = processTag(tag, workspaceId?.toString() || undefined, evaluateMetadataMap);
        const tagContent = !useBadge ? (
          <OverflowDisplayTooltip content={tag}>
            <span
              className={`flex items-center gap-1.5 truncate transition-colors duration-200 ease-in-out ${
                isLink ? "text-blue-600 hover:text-blue-700" : ""
              }`}
            >
              {isLink && <ExternalLinkIcon className="h-3 w-3 flex-shrink-0" />}
              {displayText}
            </span>
          </OverflowDisplayTooltip>
        ) : (
          <Badge
            variant={isLink ? BadgeVariant.BLUE : BadgeVariant.GRAY}
            title={tag}
            className={`min-w-0 max-w-full shrink truncate transition-all duration-200 ease-in-out ${
              isLink ? "hover:opacity-80" : ""
            }`}
          >
            <OverflowDisplayTooltip content={tag}>
              <span className="flex items-center gap-1.5 truncate transition-colors duration-200 ease-in-out">
                {isLink && <ExternalLinkIcon className="h-3 w-3 flex-shrink-0" />}
                {displayText}
              </span>
            </OverflowDisplayTooltip>
          </Badge>
        );

        const tagWrapper = (
          <div
            key={tag}
            className={`flex min-w-0 shrink items-center gap-x-1.5 text-xs transition-colors duration-200 ease-in-out ${
              isLink ? "text-blue-600" : "text-gray-500"
            } ${!useBadge ? "max-w-full truncate" : ""}`}
          >
            {tagContent}
            {idx < displayTags.length - 1 && separator && (
              <span className="shrink-0 transition-colors duration-200 ease-in-out">
                {separator}
              </span>
            )}
          </div>
        );

        return tagLinkPath ? (
          <Link
            key={tag}
            to={tagLinkPath}
            onClick={e => {
              e.stopPropagation();
            }}
            className="no-underline"
          >
            {tagWrapper}
          </Link>
        ) : (
          tagWrapper
        );
      })}
      {remainingTags.length > 0 && (
        <div className="flex min-w-0 shrink-0 items-center gap-x-1.5 transition-colors duration-200 ease-in-out">
          <BasicTooltip content={remainingTags.join(", ")}>
            <span className="inline-block whitespace-nowrap px-1 text-xs text-gray-400 transition-colors duration-200 ease-in-out">
              +{remainingTags.length} more
            </span>
          </BasicTooltip>
        </div>
      )}
    </div>
  );
};
